import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { ArticleNodeGroup } from '@texas/api/endpoints/articlesApi';
import { VersionOverviewProvider } from '@texas/components/article/variant/versions/shared/VersionOverviewContext';
import { VersionsFilters } from '@texas/components/article/variant/versions/shared/VersionsFilters';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { Icons } from '@texas/components/shared/Icons';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { EntityDirectoryEmptyState } from '@texas/components/shared/entityDirectory/EntityDirectoryEmptyState';
import { EntityDirectoryFilterBar } from '@texas/components/shared/entityDirectory/EntityDirectoryFilterBar';
import { EntityDirectoryPage } from '@texas/components/shared/entityDirectory/EntityDirectoryPage';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ReactSelectOption } from '@texas/types';
import { useTranslation } from 'react-i18next';
import { InquiryLifecycle, InquiryLifecycleTabs } from './InquiryLifecycleTabs';
import { InquiryMasterTable } from './InquiryMasterTable';
import { NoVersionsNote } from './NoVersionsNote';
import { useInquiries } from './useInquiries';

interface Props {
  variantId: number;
  fixedSupplierIds?: number[];
  articleNodeGroup?: ArticleNodeGroup;
  initialBranchId?: number;
  initialSupplierId?: number;
  initialLifecycle?: InquiryLifecycle;
}

export function Inquiries({
  variantId,
  fixedSupplierIds,
  articleNodeGroup,
  initialBranchId,
  initialSupplierId,
  initialLifecycle = 'all',
}: Props) {
  const mode =
    articleNodeGroup?.nodeXValues.length || articleNodeGroup?.nodeYValues.length
      ? 'matrix'
      : 'non-matrix';
  const showArchivedInitially =
    initialLifecycle === 'all' || initialLifecycle === 'archived';

  return (
    <VersionOverviewProvider
      initialFilters={
        initialBranchId || initialSupplierId || showArchivedInitially
          ? {
              ...(initialBranchId && { branchIds: [initialBranchId] }),
              ...(initialSupplierId && {
                supplierIds: [initialSupplierId],
              }),
              ...(showArchivedInitially && { showArchived: true }),
            }
          : undefined
      }
    >
      <InquiriesInner
        variantId={variantId}
        mode={mode}
        fixedSupplierIds={fixedSupplierIds}
        articleNodeGroup={articleNodeGroup}
        initialBranchId={initialBranchId}
        initialLifecycle={initialLifecycle}
      />
    </VersionOverviewProvider>
  );
}

function InquiriesInner({
  variantId,
  mode,
  fixedSupplierIds,
  articleNodeGroup,
  initialBranchId,
  initialLifecycle,
}: {
  variantId: number;
  mode: 'matrix' | 'non-matrix';
  fixedSupplierIds?: number[];
  articleNodeGroup?: ArticleNodeGroup;
  initialBranchId?: number;
  initialLifecycle?: InquiryLifecycle;
}) {
  const { t } = useTranslation();
  const {
    activeLifecycle,
    allSlots,
    createInquiry,
    displayCurrencyId,
    displayCurrencyOptions,
    displayedInquiries,
    error,
    handleSearchChange,
    hasLoadedPageData,
    lifecycleCounts,
    loading,
    searchInput,
    setDisplayCurrencyId,
    tableSlots,
    tierColumns,
    updateLifecycle,
    versions,
  } = useInquiries({
    variantId,
    mode,
    fixedSupplierIds,
    initialLifecycle,
  });

  return (
    <BodyOverride>
      <EntityDirectoryPage
        actions={
          <Menu>
            <MenuButton
              as={Button}
              leftIcon={<Icons.Plus boxSize={4} />}
              variant="texas-solid"
              isDisabled={
                activeLifecycle === 'archived' ||
                activeLifecycle === 'all' ||
                allSlots.length === 0
              }
            >
              {t('inquiry.createInquiry')}
            </MenuButton>
            <MenuList>
              {allSlots.map((slot) => (
                <MenuItem
                  key={slot.compositionGroupPurchaseInfoId}
                  fontSize="sm"
                  onClick={() =>
                    createInquiry(slot.compositionGroupPurchaseInfoId)
                  }
                >
                  <Flex direction="column">
                    <Text>{slot.supplierName}</Text>
                    <Text color="gray.400" fontSize="xs">
                      {slot.branchIdentifier}
                      {` · V${slot.version}`}
                      {slot.basedOnGroupVersion
                        ? ` / O${slot.basedOnGroupVersion}`
                        : ''}
                    </Text>
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        }
        description={t('inquiry.overviewDescription')}
        headingLineHeight="38px"
        headingSize="32px"
        maxW="none"
        minH="calc(100dvh - 170px)"
        pb={32}
        px={0}
        py={0}
        title={t('inquiry.inquiries')}
        titleTextTransform="uppercase"
      >
        <InquiryLifecycleTabs
          active={activeLifecycle}
          counts={lifecycleCounts}
          onChange={updateLifecycle}
          filters={
            <EntityDirectoryFilterBar
              actions={
                <FormControl flexShrink={0} w="200px">
                  <FormLabel
                    position="absolute"
                    w="1px"
                    h="1px"
                    overflow="hidden"
                  >
                    {t('inquiry.showInCurrency')}
                  </FormLabel>
                  <TexasSelect<ReactSelectOption>
                    borderRadius={0}
                    isClearable={true}
                    minHeight="38px"
                    placeholder={t('inquiry.originalCurrency')}
                    value={
                      displayCurrencyId != null
                        ? displayCurrencyOptions.find(
                            (option) => option.value === displayCurrencyId,
                          ) ?? null
                        : null
                    }
                    onChange={(selected) =>
                      setDisplayCurrencyId(
                        selected ? Number(selected.value) : null,
                      )
                    }
                    options={displayCurrencyOptions}
                  />
                </FormControl>
              }
              py={3}
            >
              <Box flex="1 1 240px" maxW={{ base: 'full', lg: '340px' }}>
                <SearchFilterInput
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder={t('inquiry.searchPlaceholder')}
                  style="entityDirectory"
                />
              </Box>
              <VersionsFilters
                variantId={variantId}
                articleNodes={articleNodeGroup}
                hideMatrixFilters={true}
                hideArchivedFilter={true}
                compact={true}
                initialBranchId={initialBranchId}
              />
            </EntityDirectoryFilterBar>
          }
        >
          {(loading || !hasLoadedPageData) && (
            <Flex justify="center" py={12}>
              <Spinner />
            </Flex>
          )}
          {error && <ErrorDetails error={error} />}
          {hasLoadedPageData &&
            !loading &&
            versions.length !== 0 &&
            tableSlots.length === 0 && (
              <EntityDirectoryEmptyState>
                {t('general.noResult')}
              </EntityDirectoryEmptyState>
            )}

          {hasLoadedPageData && !loading && versions.length === 0 && (
            <NoVersionsNote />
          )}

          {!loading && tableSlots.length > 0 && (
            <InquiryMasterTable
              mode={mode}
              slots={tableSlots}
              inquiries={displayedInquiries}
              tierColumns={tierColumns}
              displayCurrencyId={displayCurrencyId}
              onCreateInquiry={createInquiry}
            />
          )}
        </InquiryLifecycleTabs>
      </EntityDirectoryPage>
    </BodyOverride>
  );
}
